import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical, Save, Loader2, LayoutList, Columns, Eye, EyeOff,
  Share2, Plus, Trash2, ExternalLink,
} from "lucide-react";
import { settingsApi } from "@/integrations/mysql/client";
import {
  useNavConfig,
  type NavConfigItem, type FooterSection, type SocialLinkItem, type SocialNetwork,
  DEFAULT_HEADER_ITEMS, DEFAULT_FOOTER_SECTIONS, DEFAULT_SOCIAL_LINKS,
} from "@/hooks/useNavConfig";
import { SOCIAL_META } from "@/components/SocialLinks";
import { useToast } from "@/hooks/use-toast";

// ── Shared UI ──────────────────────────────────────────────────────────────────

function Tab({ id, active, onClick, icon: Icon, label }: {
  id: string; active: boolean; onClick: (id: string) => void;
  icon: React.ElementType; label: string;
}) {
  return (
    <button
      onClick={() => onClick(id)}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
        active ? "border-brand-primary text-brand-primary" : "border-transparent text-gray-400 hover:text-gray-700"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

// ── Sortable nav item row ──────────────────────────────────────────────────────

function SortableNavRow({
  item, onChange,
}: {
  item: NavConfigItem;
  onChange: (updated: NavConfigItem) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl px-3 py-2.5"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none">
        <GripVertical className="w-4 h-4" />
      </div>

      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={item.label}
          onChange={e => onChange({ ...item, label: e.target.value })}
          className="w-full text-sm font-medium text-gray-800 bg-transparent border-b border-transparent hover:border-gray-200 focus:border-brand-primary focus:outline-none transition-colors py-0.5"
          maxLength={40}
        />
        <span className="text-xs text-gray-400">{item.path}</span>
      </div>

      <button
        onClick={() => onChange({ ...item, enabled: !item.enabled })}
        title={item.enabled ? "Ocultar no menu" : "Mostrar no menu"}
        className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-colors ${
          item.enabled
            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
            : "bg-gray-100 text-gray-400 hover:bg-gray-200"
        }`}
      >
        {item.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
        {item.enabled ? "Visível" : "Oculto"}
      </button>
    </div>
  );
}

// ── Header tab ─────────────────────────────────────────────────────────────────

function HeaderTab() {
  const { headerItems } = useNavConfig();
  const [items, setItems] = useState<NavConfigItem[]>(DEFAULT_HEADER_ITEMS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (headerItems.length > 0) {
      // Merge: keep DB order/settings, append any new defaults not yet in DB
      const merged = [...headerItems];
      DEFAULT_HEADER_ITEMS.forEach(def => {
        if (!merged.find(i => i.id === def.id)) {
          merged.push(def);
        }
      });
      setItems(merged);
      setDirty(false);
    }
  }, [headerItems]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const from = prev.findIndex(x => x.id === active.id);
      const to   = prev.findIndex(x => x.id === over.id);
      return arrayMove(prev, from, to);
    });
    setDirty(true);
  };

  const handleChange = (updated: NavConfigItem) => {
    setItems(prev => prev.map(x => x.id === updated.id ? updated : x));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update("header_nav_config", JSON.stringify(items));
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      toast({ title: "Menu do cabeçalho salvo!" });
      setDirty(false);
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setItems(DEFAULT_HEADER_ITEMS); setDirty(true); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Arraste para reordenar · Clique no rótulo para renomear · Toggle para mostrar/ocultar</p>
        <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors">
          Restaurar padrão
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(x => x.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1.5">
            {items.map(item => (
              <SortableNavRow key={item.id} item={item} onChange={handleChange} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {dirty && (
        <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
          <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Footer section block (sortable items) ─────────────────────────────────────

function FooterSectionBlock({
  section, onChange,
}: {
  section: FooterSection;
  onChange: (updated: FooterSection) => void;
}) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = section.items.findIndex(x => x.id === active.id);
    const to   = section.items.findIndex(x => x.id === over.id);
    onChange({ ...section, items: arrayMove(section.items, from, to) });
  };

  const handleItemChange = (updated: NavConfigItem) =>
    onChange({ ...section, items: section.items.map(x => x.id === updated.id ? updated : x) });

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-100">
        <input
          type="text"
          value={section.title}
          onChange={e => onChange({ ...section, title: e.target.value })}
          maxLength={40}
          className="text-xs font-bold text-gray-500 uppercase tracking-wider bg-transparent border-b border-transparent hover:border-gray-300 focus:border-brand-primary focus:outline-none transition-colors w-full py-0.5"
        />
      </div>
      <div className="p-2 space-y-1.5">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={section.items.map(x => x.id)} strategy={verticalListSortingStrategy}>
            {section.items.map(item => (
              <SortableNavRow key={item.id} item={item} onChange={handleItemChange} />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
}

// ── Footer tab ─────────────────────────────────────────────────────────────────

function FooterTab() {
  const { footerSections } = useNavConfig();
  const [sections, setSections] = useState<FooterSection[]>(DEFAULT_FOOTER_SECTIONS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (footerSections.length > 0) {
      setSections(footerSections);
      setDirty(false);
    }
  }, [footerSections]);

  const handleSectionChange = (updated: FooterSection) => {
    setSections(prev => prev.map(s => s.id === updated.id ? updated : s));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update("footer_nav_config", JSON.stringify(sections));
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      toast({ title: "Rodapé salvo!" });
      setDirty(false);
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setSections(DEFAULT_FOOTER_SECTIONS); setDirty(true); };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Arraste para reordenar dentro de cada coluna · Clique no rótulo para renomear</p>
        <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors">
          Restaurar padrão
        </button>
      </div>

      <div className="grid gap-3">
        {sections.map(section => (
          <FooterSectionBlock key={section.id} section={section} onChange={handleSectionChange} />
        ))}
      </div>

      {dirty && (
        <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
          <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Social networks tab ────────────────────────────────────────────────────────

const NETWORK_OPTIONS: SocialNetwork[] = [
  'instagram','facebook','youtube','linkedin','x','tiktok','whatsapp','telegram','pinterest','spotify',
];

function SortableSocialRow({
  item, onChange, onDelete,
}: {
  item: SocialLinkItem;
  onChange: (updated: SocialLinkItem) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: item.id });
  const meta = SOCIAL_META[item.network];
  const Icon = meta?.icon;

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 }}
      className="bg-white border border-gray-100 rounded-xl px-3 py-3 space-y-2.5"
    >
      <div className="flex items-center gap-2">
        {/* Drag handle */}
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 touch-none shrink-0">
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Icon + name — fixed, chosen at creation */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {Icon && (
            <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              <Icon size={16} className="text-gray-600" />
            </div>
          )}
          <span className="text-sm font-semibold text-gray-700">{meta?.label ?? item.network}</span>
        </div>

        {/* Visibility toggle */}
        <button
          onClick={() => onChange({ ...item, enabled: !item.enabled })}
          title={item.enabled ? "Ocultar" : "Mostrar"}
          className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full transition-colors shrink-0 ${
            item.enabled
              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
              : "bg-gray-100 text-gray-400 hover:bg-gray-200"
          }`}
        >
          {item.enabled ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
          {item.enabled ? "Visível" : "Oculto"}
        </button>

        {/* Delete */}
        <button
          onClick={() => onDelete(item.id)}
          className="text-gray-300 hover:text-red-500 transition-colors shrink-0"
          title="Remover"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* URL field */}
      <div className="flex items-center gap-2 pl-6">
        <ExternalLink className="w-3.5 h-3.5 text-gray-300 shrink-0" />
        <input
          type="url"
          value={item.url}
          onChange={e => onChange({ ...item, url: e.target.value })}
          placeholder="https://..."
          className="flex-1 text-xs font-mono border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all"
        />
      </div>
    </div>
  );
}

// ── Network picker panel ───────────────────────────────────────────────────────

function NetworkPickerPanel({ usedNetworks, onPick, onClose }: {
  usedNetworks: Set<SocialNetwork>;
  onPick: (network: SocialNetwork) => void;
  onClose: () => void;
}) {
  return (
    <div className="border-2 border-brand-primary/20 rounded-2xl bg-white shadow-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-gray-600 uppercase tracking-wide">Escolha a rede social</p>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xs underline transition-colors">Cancelar</button>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {NETWORK_OPTIONS.map(n => {
          const meta = SOCIAL_META[n];
          const Icon = meta.icon;
          const already = usedNetworks.has(n);
          return (
            <button
              key={n}
              onClick={() => !already && onPick(n)}
              disabled={already}
              title={already ? `${meta.label} já adicionado` : `Adicionar ${meta.label}`}
              className={`flex flex-col items-center gap-1.5 rounded-xl p-3 transition-all border ${
                already
                  ? 'border-gray-100 bg-gray-50 opacity-40 cursor-not-allowed'
                  : 'border-gray-100 hover:border-brand-primary/30 hover:bg-brand-primary/5 hover:scale-105 cursor-pointer'
              }`}
            >
              <Icon size={22} className={already ? 'text-gray-300' : 'text-gray-600'} />
              <span className="text-[10px] font-medium text-gray-500 leading-tight text-center">{meta.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SocialTab() {
  const { socialLinks } = useNavConfig();
  const [items, setItems] = useState<SocialLinkItem[]>(DEFAULT_SOCIAL_LINKS);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const { toast } = useToast();
  const qc = useQueryClient();

  useEffect(() => {
    if (socialLinks.length > 0) {
      setItems(socialLinks);
      setDirty(false);
    }
  }, [socialLinks]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setItems(prev => {
      const from = prev.findIndex(x => x.id === active.id);
      const to   = prev.findIndex(x => x.id === over.id);
      return arrayMove(prev, from, to);
    });
    setDirty(true);
  };

  const handleChange = (updated: SocialLinkItem) => {
    setItems(prev => prev.map(x => x.id === updated.id ? updated : x));
    setDirty(true);
  };

  const handleDelete = (id: string) => {
    setItems(prev => prev.filter(x => x.id !== id));
    setDirty(true);
  };

  const handlePick = (network: SocialNetwork) => {
    const id = `${network}_${Date.now()}`;
    setItems(prev => [...prev, { id, network, url: '', enabled: true }]);
    setShowPicker(false);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update("social_links_config", JSON.stringify(items));
      qc.invalidateQueries({ queryKey: ["site-settings"] });
      toast({ title: "Redes sociais salvas!" });
      setDirty(false);
    } catch {
      toast({ title: "Erro ao salvar", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setItems(DEFAULT_SOCIAL_LINKS); setShowPicker(false); setDirty(true); };

  const usedNetworks = new Set(items.map(x => x.network));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-400">Arraste para reordenar · Cole o link · Toggle para mostrar/ocultar</p>
        <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors">
          Restaurar padrão
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map(x => x.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map(item => (
              <SortableSocialRow key={item.id} item={item} onChange={handleChange} onDelete={handleDelete} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {showPicker ? (
        <NetworkPickerPanel
          usedNetworks={usedNetworks}
          onPick={handlePick}
          onClose={() => setShowPicker(false)}
        />
      ) : (
        <button
          onClick={() => setShowPicker(true)}
          className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-brand-primary/40 hover:bg-brand-primary/5 text-gray-400 hover:text-brand-primary text-sm font-medium py-2.5 rounded-xl transition-all"
        >
          <Plus className="w-4 h-4" />
          Adicionar rede social
        </button>
      )}

      {dirty && (
        <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
          <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Salvar
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────

export function NavManager() {
  const [tab, setTab] = useState<"header" | "footer" | "social">("header");

  return (
    <div className="space-y-5">
      <div className="flex gap-0 border-b border-gray-100">
        <Tab id="header" active={tab === "header"} onClick={t => setTab(t as any)} icon={LayoutList} label="Cabeçalho" />
        <Tab id="footer" active={tab === "footer"} onClick={t => setTab(t as any)} icon={Columns}    label="Rodapé" />
        <Tab id="social" active={tab === "social"} onClick={t => setTab(t as any)} icon={Share2}     label="Redes Sociais" />
      </div>

      {tab === "header" && <HeaderTab />}
      {tab === "footer" && <FooterTab />}
      {tab === "social" && <SocialTab />}
    </div>
  );
}
